import { api } from '../../../api/client';
import type { Field, QcRow, SavedChart, Stat } from './controlChartData';

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Control Charts — typed API client.
//
// The screen used to run on a seeded generator; it now runs on the LIMS's own
// QC results.  Everything that decides what a chart SAYS — which column is
// plotted, the mean, the SD, the control limits, the in/out-of-control count —
// is computed by the API (CLAUDE.md §19.1: no business logic in the UI).  This
// file only carries the shapes across.
//
//   GET    /api/v1/control-charts             saved list
//   GET    /api/v1/control-charts/tests       the Test lookup + record counts
//   POST   /api/v1/control-charts/facets      cascading counts for the panel
//   POST   /api/v1/control-charts/retrieve    Load — points + limits
//   GET    /api/v1/control-charts/{id}        reopen a saved chart
//   GET    /api/v1/control-charts/{id}/points its points, as signed
//   POST   /api/v1/control-charts             Save & sign
//   DELETE /api/v1/control-charts/{id}
// ─────────────────────────────────────────────────────────────────────────────

/** A row of the Test lookup. `records` is how many chartable QC results sit
 *  behind the choice — shown so the analyst can see that before picking. */
export interface ApiTestOption {
  testMethodId: string;
  matrix: string | null;
  test: string | null;
  method: string | null;
  records: number;
  /** True when the test is a microbiology method (Colilert, membrane filter,
   *  plate count, etc.): it charts on SM 9020 B rules — range / individuals-on-
   *  log / sterility — instead of the chemistry Levey-Jennings ones. Drives the
   *  micro/chemistry toggle's test filtering. */
  isMicro: boolean;
}

export interface ApiFacet { value: string; count: number }
export interface ApiQcTypeFacet { value: string; role: string | null; rootRole: string | null; count: number }

/** Everything the query panel needs for the current selection, in one call. */
export interface ApiFacets {
  totalRecords: number;
  minDate: string | null;
  maxDate: string | null;
  roles: ApiFacet[];
  qcTypes: ApiQcTypeFacet[];
  parameters: ApiFacet[];
  instruments: ApiFacet[];
  analysts: ApiFacet[];
}

/** The query panel's current selection. One shape for facets, Load and Save,
 *  so the three cannot drift apart. */
export interface ApiScope {
  testMethodId: string;
  from: string | null;
  to: string | null;
  instruments: string[];
  analysts: string[];
  role: string | null;
  qcTypes: string[];
  parameters: string[];
  /** Charted-column override (`rec` | `rpd` | `res`). Null lets the API
   *  auto-derive from the QC root roles; the Trend popup's RPD panel forces
   *  `rpd` because a D-member's root role is Spike, which would otherwise chart
   *  recovery. Only honoured by the read-only `series` endpoint. */
  field?: string | null;
  /** Charting mode — `chemistry` (default / null) or `micro`. In micro mode the
   *  QC roles map to the SM 9020 B charts (Duplicate → range, positive control →
   *  individuals on log₁₀ count, Blank → sterility attribute) and the value is
   *  read from the raw count in `Result`, not `Rec`/`ResultNumeric`. */
  mode?: string | null;
}

export interface ApiDropped { lost: number; field: Field; types: string[] }

export interface ApiQueryResult {
  field: Field;
  rows: QcRow[];
  stats: Stat[];
  dropped: ApiDropped | null;
}

export interface ApiSavedDetail {
  header: SavedChart;
  testMethodId: string;
  qcTypes: string[];
  parameters: string[];
  instruments: string[];
  analysts: string[];
  role: string | null;
}

export interface ApiSaveResult {
  oid: string;
  ccid: string;
  /** QC results the chart actually took ownership of — can be lower than what
   *  was on screen if another chart claimed some first. */
  claimed: number;
  row: SavedChart;
}

export interface SignInput {
  meaning: string;
  comment: string | null;
  password: string;
  code: string;
  certified: boolean;
}

type Envelope<T> = { success: boolean; data: T };

const unwrap = <T,>(d: Envelope<T>) => d.data;

export async function fetchSavedCharts(signal?: AbortSignal): Promise<SavedChart[]> {
  const { data } = await api.get<Envelope<SavedChart[]>>('/api/v1/control-charts', { signal });
  return unwrap(data) ?? [];
}

export async function fetchTests(signal?: AbortSignal): Promise<ApiTestOption[]> {
  const { data } = await api.get<Envelope<ApiTestOption[]>>('/api/v1/control-charts/tests', { signal });
  return unwrap(data) ?? [];
}

export async function fetchFacets(scope: ApiScope, signal?: AbortSignal): Promise<ApiFacets> {
  const { data } = await api.post<Envelope<ApiFacets>>('/api/v1/control-charts/facets', scope, { signal });
  return unwrap(data);
}

export async function retrieve(scope: ApiScope, signal?: AbortSignal): Promise<ApiQueryResult> {
  const { data } = await api.post<Envelope<ApiQueryResult>>('/api/v1/control-charts/retrieve', scope, { signal });
  return unwrap(data);
}

/** Read-only QC series for the SDMS Trend-icon popup: the SAME engine + limits
 *  as {@link retrieve}, but WITHOUT the "unclaimed only" filter (so a series a
 *  saved chart already owns still charts) and honouring `scope.field`. Not
 *  audited as a Preview — the Trend icon is a per-row hover, not a data pull. */
export async function fetchSeries(scope: ApiScope, signal?: AbortSignal): Promise<ApiQueryResult> {
  const { data } = await api.post<Envelope<ApiQueryResult>>('/api/v1/control-charts/series', scope, { signal });
  return unwrap(data);
}

export async function fetchSavedDetail(id: string, signal?: AbortSignal): Promise<ApiSavedDetail> {
  const { data } = await api.get<Envelope<ApiSavedDetail>>(`/api/v1/control-charts/${id}`, { signal });
  return unwrap(data);
}

export async function fetchSavedPoints(id: string, signal?: AbortSignal): Promise<ApiQueryResult> {
  const { data } = await api.get<Envelope<ApiQueryResult>>(`/api/v1/control-charts/${id}/points`, { signal });
  return unwrap(data);
}

/** Save and sign. The password and the 6-digit code are verified server-side
 *  and never persisted (§18.12) — they are sent once and forgotten. */
export async function saveChart(scope: ApiScope, sig: SignInput): Promise<ApiSaveResult> {
  const { data } = await api.post<Envelope<ApiSaveResult>>('/api/v1/control-charts', {
    scope,
    meaning: sig.meaning,
    comment: sig.comment,
    password: sig.password,
    code: sig.code,
    certified: sig.certified,
  });
  return unwrap(data);
}

export async function deleteChart(id: string): Promise<void> {
  await api.delete(`/api/v1/control-charts/${id}`);
}

/** The real audit trail for this form, from the shared endpoint every other
 *  form uses (§14b.2) — the chart and its signature are two FormNames, read
 *  together so one dialog shows a chart's whole life. */
export interface ApiAuditRow {
  oid: string;
  createdDate?: string | null;
  createdBy?: string | null;
  operationType?: string | null;
  formName?: string | null;
  sourceId?: string | null;
  propertyName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  comment?: string | null;
  reasonForChange?: string | null;
}

export const AUDIT_FORMS = 'Control Chart,Control Chart Signing';

export async function fetchAudit(sourceId?: string | null, signal?: AbortSignal): Promise<ApiAuditRow[]> {
  const { data } = await api.get<Envelope<ApiAuditRow[]>>('/api/v1/audit/trail', {
    params: { formNames: AUDIT_FORMS, sourceId: sourceId ?? undefined },
    signal,
  });
  return unwrap(data) ?? [];
}
